import React, { useState, useRef, useEffect } from 'react';
import { useUser } from '../contexts/UserContext';

const FAQ = [
  { q: 'How do I create clips?', a: 'Go to the Generator, paste a YouTube URL, choose your caption style and settings, then click Generate. Your clips will be ready in 2-5 minutes.' },
  { q: 'What caption styles are available?', a: 'We have 11 styles: Minimal, Word Highlight, Hormozi Bold, CapCut Pop-In, Kinetic Zoom, Fight Hype, Karaoke, Bold Pop, Explosion, Gradient Pop, and Flash. Free plan gets Minimal only — Pro/Business unlock all 11 styles with 6 colour options.' },
  { q: 'How does batch processing work?', a: 'Batch processing lets you paste multiple YouTube URLs (3 for Pro, 4 for Business) and process them all at once in parallel. Go to the Batch Processor tab to use it.' },
  { q: 'How do I translate captions?', a: 'In the Generator or Batch Processor, enable the "Translate Captions" option and pick your target language. Captions will be translated to that language using AI.' },
  { q: 'Why do my clips have a watermark?', a: 'Free plan clips include a small watermark. Upgrade to Creator Pro (just \u00a39.99/mo) to remove it and get 1080p output.' },
  { q: 'How long are clips stored?', a: 'Pro plan clips are stored for 5 hours, Business for 10 hours. Free clips are not stored. Download them as soon as they\'re ready.' },
  { q: 'What is the emoji overlay?', a: 'The emoji overlay detects keywords in your clip and shows relevant emoji images on screen. Set it to "Auto" for automatic detection, or pick a specific emoji.' },
  { q: 'What plans are available?', a: 'Free (5 videos/mo, 3 clips each, 720p watermarked, Minimal captions only), Creator Pro at £9.99/mo (20 videos, 5 clips each, 1080p, all 11 styles, emoji overlays, AI hooks, batch processing, no watermark), and Business at £24.99/mo (60 videos, 8 clips each, custom branding, priority queue).' },
];

const HelpChat = () => {
  const { user } = useUser();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [showFaq, setShowFaq] = useState(true);
  const [cooldown, setCooldown] = useState(false);
  const messagesEnd = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (messagesEnd.current) messagesEnd.current.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  useEffect(() => {
    if (isOpen && inputRef.current) inputRef.current.focus();
  }, [isOpen]);

  // Listen for mobile bottom nav "Help" tap
  useEffect(() => {
    const handler = () => setIsOpen(true);
    window.addEventListener('open-help-chat', handler);
    return () => window.removeEventListener('open-help-chat', handler);
  }, []);

  const addMessage = (role, text) => {
    setMessages(prev => [...prev, { role, text, time: new Date() }]);
  };

  const handleFaqClick = (faq) => {
    addMessage('user', faq.q);
    addMessage('assistant', faq.a);
    setShowFaq(false);
  };

  const handleSend = async () => {
    const msg = input.trim();
    if (!msg || loading || cooldown) return;

    addMessage('user', msg);
    setInput('');
    setShowFaq(false);

    if (!user?.isAuthenticated) {
      addMessage('assistant', 'Please sign in to use the AI assistant. You can browse the FAQ topics above without signing in.');
      return;
    }

    setLoading(true);
    setCooldown(true);
    setTimeout(() => setCooldown(false), 2000);

    try {
      const tk = localStorage.getItem('auth_token');
      // Detect Google Translate language so AI responds in same language
      const langMatch = document.cookie.match(/googtrans=\/en\/([a-z-]+)/i);
      const uiLang = langMatch ? langMatch[1] : '';
      const r = await fetch('/api/chat/ask', {
        method: 'POST',
        headers: { 'Authorization': 'Bearer ' + tk, 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: msg, language: uiLang })
      });
      const d = await r.json();
      if (r.ok) {
        addMessage('assistant', d.answer);
        if (d.remaining <= 2 && d.remaining > 0) {
          addMessage('system', d.remaining + ' question' + (d.remaining === 1 ? '' : 's') + ' remaining today.');
        }
      } else {
        addMessage('assistant', d.error || 'Something went wrong. Please try again.');
      }
    } catch (e) {
      addMessage('assistant', 'Connection error. Please check your internet and try again.');
    }
    setLoading(false);
  };

  // Floating button + chat panel
  return (
    <>
      {/* Floating help button (hidden on mobile — bottom nav has Help tab) */}
      {!isOpen && (
        <button
          className="help-chat-fab"
          onClick={() => setIsOpen(true)}
          aria-label="Help assistant"
          style={{
            position: 'fixed', bottom: '24px', right: '24px', zIndex: 9998,
            width: '56px', height: '56px', borderRadius: '50%',
            background: 'linear-gradient(135deg, #8b5cf6, #06b6d4)',
            border: 'none', cursor: 'pointer',
            boxShadow: '0 4px 20px rgba(139,92,246,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'all 0.3s ease', fontSize: '24px'
          }}
          onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.1)'; e.currentTarget.style.boxShadow = '0 6px 28px rgba(139,92,246,0.6)'; }}
          onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = '0 4px 20px rgba(139,92,246,0.5)'; }}
        >
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
          </svg>
        </button>
      )}

      {/* Chat panel */}
      {isOpen && (
        <div className="help-chat-panel" style={{
          position: 'fixed', bottom: '24px', right: '24px', zIndex: 9998,
          width: '380px', maxWidth: 'calc(100vw - 32px)',
          height: '520px', maxHeight: 'calc(100vh - 100px)',
          background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 100%)',
          border: '1px solid rgba(139,92,246,0.3)',
          borderRadius: '20px',
          boxShadow: '0 10px 40px rgba(0,0,0,0.5)',
          display: 'flex', flexDirection: 'column',
          overflow: 'hidden'
        }}>
          {/* Header */}
          <div style={{
            padding: '16px 20px',
            background: 'linear-gradient(135deg, #8b5cf6, #06b6d4)',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            flexShrink: 0
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{
                width: '34px', height: '34px', borderRadius: '50%',
                background: 'rgba(255,255,255,0.2)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '18px'
              }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                </svg>
              </div>
              <div>
                <div style={{ color: 'white', fontSize: '14px', fontWeight: '700' }}>Help Assistant</div>
                <div style={{ color: 'rgba(255,255,255,0.75)', fontSize: '11px' }}>Ask me anything about VlogClip AI</div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '6px' }}>
              {messages.length > 0 && (
                <button
                  onClick={() => { setMessages([]); setShowFaq(true); }}
                  title="New conversation"
                  style={{
                    background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '50%',
                    width: '30px', height: '30px', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: 'white', fontSize: '14px',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.3)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.15)'; }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/>
                  </svg>
                </button>
              )}
              <button
                onClick={() => setIsOpen(false)}
                title="Close"
                style={{
                  background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '50%',
                  width: '30px', height: '30px', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: 'white', fontSize: '16px', fontWeight: '700',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.3)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.15)'; }}
              >
                &times;
              </button>
            </div>
          </div>

          {/* Messages area */}
          <div style={{
            flex: 1, overflowY: 'auto', padding: '16px',
            display: 'flex', flexDirection: 'column', gap: '12px'
          }}>
            {/* Welcome + FAQ */}
            {messages.length === 0 && (
              <>
                <div style={{
                  background: 'rgba(139,92,246,0.1)', border: '1px solid rgba(139,92,246,0.2)',
                  borderRadius: '14px', padding: '14px 16px',
                  color: '#cbd5e1', fontSize: '13px', lineHeight: '1.5'
                }}>
                  Hi{user?.username ? ' ' + user.username : ''}! I can help you with anything about VlogClip AI. Pick a topic below or type your own question.
                </div>
                {showFaq && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {FAQ.map((faq, i) => (
                      <button key={i} onClick={() => handleFaqClick(faq)}
                        style={{
                          background: 'rgba(255,255,255,0.04)',
                          border: '1px solid rgba(255,255,255,0.08)',
                          borderRadius: '10px', padding: '10px 14px',
                          color: '#a5b4fc', fontSize: '12px', fontWeight: '500',
                          cursor: 'pointer', textAlign: 'left',
                          transition: 'all 0.15s'
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = 'rgba(139,92,246,0.12)'; e.currentTarget.style.borderColor = 'rgba(139,92,246,0.3)'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; }}
                      >
                        {faq.q}
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* Chat messages */}
            {messages.map((msg, i) => (
              <div key={i} style={{
                display: 'flex',
                justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start'
              }}>
                {msg.role === 'system' ? (
                  <div style={{
                    width: '100%', textAlign: 'center',
                    color: '#64748b', fontSize: '11px', fontStyle: 'italic',
                    padding: '4px 0'
                  }}>{msg.text}</div>
                ) : (
                  <div style={{
                    maxWidth: '85%',
                    padding: '10px 14px',
                    borderRadius: msg.role === 'user' ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                    background: msg.role === 'user'
                      ? 'linear-gradient(135deg, #8b5cf6, #7c3aed)'
                      : 'rgba(255,255,255,0.06)',
                    border: msg.role === 'user' ? 'none' : '1px solid rgba(255,255,255,0.08)',
                    color: msg.role === 'user' ? 'white' : '#cbd5e1',
                    fontSize: '13px', lineHeight: '1.5',
                    wordBreak: 'break-word'
                  }}>
                    {msg.text}
                  </div>
                )}
              </div>
            ))}

            {/* Loading indicator */}
            {loading && (
              <div style={{
                display: 'flex', justifyContent: 'flex-start'
              }}>
                <div style={{
                  padding: '10px 18px',
                  borderRadius: '14px 14px 14px 4px',
                  background: 'rgba(255,255,255,0.06)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  color: '#64748b', fontSize: '13px'
                }}>
                  <span className="help-chat-typing">Thinking</span>
                </div>
              </div>
            )}

            <div ref={messagesEnd} />
          </div>

          {/* Input area */}
          <div style={{
            padding: '12px 16px', flexShrink: 0,
            borderTop: '1px solid rgba(255,255,255,0.06)',
            display: 'flex', gap: '8px', alignItems: 'center'
          }}>
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
              placeholder={user?.isAuthenticated ? 'Type your question...' : 'Sign in to ask questions...'}
              disabled={loading}
              style={{
                flex: 1, background: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: '12px', padding: '10px 14px',
                color: '#e2e8f0', fontSize: '13px',
                outline: 'none', transition: 'border-color 0.2s'
              }}
              onFocus={e => { e.currentTarget.style.borderColor = 'rgba(139,92,246,0.5)'; }}
              onBlur={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; }}
            />
            <button
              onClick={handleSend}
              disabled={loading || cooldown || !input.trim()}
              style={{
                width: '40px', height: '40px', borderRadius: '12px',
                background: input.trim() && !loading && !cooldown
                  ? 'linear-gradient(135deg, #8b5cf6, #06b6d4)'
                  : 'rgba(255,255,255,0.06)',
                border: 'none', cursor: input.trim() && !loading && !cooldown ? 'pointer' : 'default',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.2s', flexShrink: 0
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={input.trim() && !loading && !cooldown ? 'white' : '#475569'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
              </svg>
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes helpTyping {
          0%, 20% { content: '.'; }
          40% { content: '..'; }
          60%, 100% { content: '...'; }
        }
        .help-chat-typing::after {
          content: '';
          animation: helpTyping 1.2s infinite;
        }
        .help-chat-panel {
          background: #0f172a !important;
          animation: helpChatFadeIn 0.2s ease-out;
        }
        .help-chat-panel * { background-color: transparent; }
        .help-chat-panel input { background: rgba(255,255,255,0.06) !important; }
        @keyframes helpChatFadeIn {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @media (max-width: 1024px) {
          .help-chat-fab { display: none !important; }
          .help-chat-panel {
            bottom: 64px !important;
            right: 0 !important;
            left: 0 !important;
            width: 100% !important;
            max-width: 100% !important;
            height: calc(100vh - 128px) !important;
            max-height: calc(100vh - 128px) !important;
            border-radius: 20px 20px 0 0 !important;
          }
        }
      `}</style>
    </>
  );
};

export default HelpChat;
